import "./PhoneDialer.css"
import moment from "moment"
import { useEffect, useRef, useState } from "react";
import FlexRow from "../base/FlexRow"
import IconButton from "../base/IconButton"
import { appendLatestCalls, closeBrowserPhone, createJob, getUser, hasRole, markMissedCallsSeen, openBrowserPhone, openJobQuickView, openNewChatForPhone, pauseGlobalPlayer, playCall, playGlobalPlayer, setLatestCalls, toPrettyDate, useChatList, useClaimedExts, useExtensions, useGlobal, useHasCallRecording, useHub, useJobs, useLatestCalls, usePhone, usePhoneLines, usePhoneTabCoordinator, useTechs, useTechsAll, useUnseenMissedCallsCount } from "../../core/Global";
import { useNavigate } from "react-router-dom";
import LiveCallItem from "../calls/LiveCallItem";
import Icon from "../base/Icon";
import Modal from "../base/Modal";
import DataItem from "../../models/DataItem";
import DialPad from "./DialPad";
import { LineStateSnapshot, SipPhoneManager, SipPhoneStateSnapshot } from "../../core/SipPhoneManager";
import ActiveCall from "./ActiveCall";
import { API_ROUTES, ROLES } from "../../core/Constants";
import { toastError, toastSuccess } from "../base/Toast";
import { ExtensionCompact } from "../../modules/voice/pbx/models/Extension";
import Indicator from "../base/Indicator";
import ActiveCallCompact from "./ActiveCallCompact";
import Job from "../../modules/crm/jobs/models/Job";
import { CallSources, CallTypes } from "../../core/Enums";
import Call from "../../modules/voice/calls/models/Call";
import Environment from "../../env/env";
import Avatar from "../base/Avatar";


// Page sizes for the dialer's call log. The first paint loads a fuller list; each
// subsequent scroll fetch is kept smaller so it's cheap. The backend clamps both.
const CALLS_FIRST_PAGE_SIZE = 50
const CALLS_PAGE_SIZE = 30

// Blocked-number lookup key: compare on digits only, dropping a US leading 1, so the same number
// matches regardless of how it's formatted in a call row vs stored in the block list.
function normalizeBlockKey(num?: string): string {
    if (!num) return ""
    let d = ("" + num).replace(/\D/g, "")
    if (d.length === 11 && d.startsWith("1")) d = d.substring(1)
    return d
}

type BlockInfo = { id: string, blockedByUserId?: string }
type BlockConfirmState = { number: string, mode: "block" | "unblock", id?: string }

export default function PhoneDialer() {


    const phone = usePhone()
    const phoneTabs = usePhoneTabCoordinator()
    const navigate = useNavigate()

    const hub = useHub()
    const user = getUser()
    const [globalState] = useGlobal()
    const exts = useExtensions()
    const claimedExts = useClaimedExts()
    const latestCalls = useLatestCalls()
    const chatList = useChatList()
    const phoneLines = usePhoneLines()

    const [expandedCallId, expandedCallIdSet] = useState<string | undefined>(undefined)
    const [expandedContactId, expandedContactIdSet] = useState<string | undefined>(undefined)

    // Infinite scroll for the Calls tab. `callsHasMore` gates further fetches; the ref
    // guards against onScroll firing the same fetch many times before state settles.
    const [callsLoadingMore, callsLoadingMoreSet] = useState(false)
    const [callsHasMore, callsHasMoreSet] = useState(true)
    const loadingMoreRef = useRef(false)

    const techs = useTechsAll()
    const jobs = useJobs()
    const hasCallRecording = useHasCallRecording()

    const searchInput = useRef<HTMLInputElement>(null)

    const latestTechsRef = useRef(techs)
    const latestJobsRef = useRef(jobs)

    const [callerIdName, callerIdNameSet] = useState<string>()
    const [callerIdIsTech, callerIdIsTechSet] = useState<boolean>(false)
    const [relatedJobs, relatedJobsSet] = useState<Job[]>([])

    const [connectingToExt, connectingToExtSet] = useState<ExtensionCompact>()

    // Blocked numbers for this owner, keyed by normalizeBlockKey; drives the Block/Unblock toggle.
    const [blockedMap, blockedMapSet] = useState<Record<string, BlockInfo>>({})
    const [blockedRefresh, blockedRefreshSet] = useState<number>(0)
    const [blockConfirm, blockConfirmSet] = useState<BlockConfirmState | undefined>(undefined)

    const [isDragging, setIsDragging] = useState(false)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const ref = useRef<HTMLDivElement>(null)

    const lines = phone.getLines()
    const [selectedLine, selectedLineSet] = useState<LineStateSnapshot>()

    const [extMenuOpen, extMenuOpenSet] = useState(false)
    const [viewMode, viewModeSet] = useState<"phone" | "manage">("phone")
    const [revokingExtId, revokingExtIdSet] = useState<string>()
    const extMenuRef = useRef<HTMLDivElement>(null)

    const [selectedTab, selectedTabSet] = useState<string>("dial")
    const [phoneState, setPhoneState] = useState<SipPhoneStateSnapshot>(phone.getState())

    const unseenMissed = useUnseenMissedCallsCount()

    // Clear the missed-call badge as soon as the user actually views the Calls tab.
    // We don't mark on dialer-open alone — the user might just be placing a call.
    useEffect(() => {
        if (selectedTab === "calls" && unseenMissed > 0) {
            markMissedCallsSeen()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, unseenMissed])

    type ExtClaimer = { extId: string; userId: string; userName: string; claimedAt: string }
    const [claimers, claimersSet] = useState<ExtClaimer[]>([])

    const MAX_CLAIMED_EXTS = 3

    const renderLineStatusIcon = (line: LineStateSnapshot | undefined, size = 14) => {
        const status = line?.registrationStatus;
        const connected = !!line?.transportConnected;

        if (status === "registered" && connected) {
            return <Icon icon="circle" type="solid" color="#22c55e" size={size} title="Connected" />;
        }
        if (status === "starting" || status === "started" || status === "registering") {
            return <Icon icon="circle-notch" type="solid" spin color="#eab308" size={size} title="Registering..." />;
        }
        return <Icon icon="circle-xmark" type="solid" color="#ef4444" size={size} title={line?.lastError || "Disconnected"} />;
    };

    const formatClaimedAt = (raw: string) => {
        const offset = user?.timeZoneOffset ?? 0;
        const m = moment(raw).utc().add(offset, "minutes");
        const now = moment().utc().add(offset, "minutes");
        const diffHours = now.diff(m, "hours");

        if (diffHours < 24 && now.isSame(m, "day")) {
            return m.fromNow();
        }
        if (now.clone().subtract(1, "day").isSame(m, "day")) {
            return `Yesterday at ${m.format("HH:mm")}`;
        }
        return `${m.format("MMM D")} at ${m.format("HH:mm")}`;
    };

    const pickerVisible = viewMode === "manage" || !claimedExts || claimedExts.length < 1;

    useEffect(() => {
        if (!pickerVisible) return;

        hub.Get(API_ROUTES.ExtensionClaimers, (res: any) => {
            if (res?.success && Array.isArray(res.data)) {
                claimersSet(res.data);
            }
        });
    }, [pickerVisible, connectingToExt, revokingExtId]);

    const notificationRef = useRef<Notification | null>(null)
    const notifiedCallIdRef = useRef<string | null>(null)

    const [isPhoneOwner, setIsPhoneOwner] = useState<boolean>(phoneTabs.isOwner())
    const [phoneOwnerTabId, setPhoneOwnerTabId] = useState<string | undefined>(phoneTabs.getState().ownerTabId)

    useEffect(() => {
        if (!isPhoneOwner) return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission === "default") {
            Notification.requestPermission().catch(() => undefined);
        }
    }, [isPhoneOwner])

    useEffect(() => {
        if (!isPhoneOwner) return;
        if (typeof Notification === "undefined") return;

        const incomingRinging = phoneState.calls?.find(
            c => c.direction === "incoming" && (c.status === "ringing" || c.status === "new")
        );

        if (incomingRinging) {
            if (notifiedCallIdRef.current === incomingRinging.id) return;
            if (document.hasFocus() && document.visibilityState === "visible") return;
            if (Notification.permission !== "granted") return;

            notifiedCallIdRef.current = incomingRinging.id;

            try {
                notificationRef.current?.close();
            } catch { }

            const title = "Incoming call";
            const bodyText = callerIdName
                ? `${callerIdName} • ${incomingRinging.callerId}`
                : incomingRinging.callerId || "Unknown number";

            const n = new Notification(title, {
                body: bodyText,
                tag: "rinvox-incoming-call",
                requireInteraction: true,
                icon: "/assets/media/logos/rinvox-icon.png"
            });

            n.onclick = () => {
                window.focus();
                openBrowserPhone();
                try { n.close(); } catch { }
            };

            notificationRef.current = n;
        }
        else if (notificationRef.current) {
            try { notificationRef.current.close(); } catch { }
            notificationRef.current = null;
            notifiedCallIdRef.current = null;
        }
    }, [phoneState.calls, isPhoneOwner, callerIdName])

    useEffect(() => {
        return () => {
            try { notificationRef.current?.close(); } catch { }
        };
    }, [])

    useEffect(() => {
        if (!extMenuOpen) return;

        const onDocClick = (e: MouseEvent) => {
            if (extMenuRef.current && !extMenuRef.current.contains(e.target as Node)) {
                extMenuOpenSet(false);
            }
        };

        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [extMenuOpen]);


    useEffect(() => {
        phoneTabs.start();

        return () => {
            phoneTabs.destroy();
        };
    }, [])



    useEffect(() => {
        const unsubscribe = phoneTabs.subscribe(async state => {
            setIsPhoneOwner(state.isOwner);
            setPhoneOwnerTabId(state.ownerTabId);

            if (!state.isOwner) {
                await phone.clearAllLines().catch(() => undefined);
            }
        });

        return () => {
            unsubscribe();
        };
    }, []);

    const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!ref.current) return;

        setIsDragging(true);

        const rect = ref.current.getBoundingClientRect();

        setPosition({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        });
    };

    const onMouseMove = (event: MouseEvent) => {
        if (!isDragging || !ref.current) return;

        const x = event.clientX - position.x;
        const y = event.clientY - position.y;

        ref.current.style.left = `${x}px`;
        ref.current.style.top = `${y}px`;
        ref.current.style.bottom = "auto";
        ref.current.style.transform = "none";
    };

    const onMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        } else {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging]);


    const [tabs] = useState<DataItem[]>([
        {
            id: "dial",
            name: "Dial",
            icon: "dialpad"
        },
        {
            id: "calls",
            name: "Calls",
            icon: "history"
        },
        {
            id: "contacts",
            name: "Contacts",
            icon: "address-book"
        },
        {
            id: "settings",
            name: "Settings",
            icon: "gear"
        }
    ])


    const [searchKeyword, searchKeywordSet] = useState("")

    useEffect(() => {
        if (selectedTab == "contacts") {
            searchKeywordSet("")
            searchInput.current?.focus()
        }
    }, [selectedTab])

    useEffect(() => {
        latestTechsRef.current = techs;
    }, [techs]);

    useEffect(() => {
        latestJobsRef.current = jobs;
    }, [jobs]);

    useEffect(() => {
        phone.setRingtoneSource("/assets/sounds/ringtone.mp3");
        const unsubscribe = phone.subscribe((state) => {
            setPhoneState(state);

            const techs = latestTechsRef.current;
            const jobs = latestJobsRef.current;

            const callerId = state.activeCall?.callerId;

            callerIdIsTechSet(false)

            if (!callerId) {
                callerIdNameSet(undefined);
                relatedJobsSet([]);
                return;
            }



            const normalizedCaller = normalizeToPhoneRaw(callerId);

            if (!normalizedCaller) {
                callerIdNameSet(undefined);
                relatedJobsSet([]);
                return;
            }

            // 1) Search in techs first
            const techMatch = techs?.find(t => t.phoneRaw === normalizedCaller);

            if (techMatch) {
                callerIdNameSet(techMatch.fullName);
                relatedJobsSet(
                    (jobs ?? []).filter(j => j.techId === techMatch.id)
                );
                callerIdIsTechSet(true)
                return;
            }

            // 2) Search in jobs by phoneRaw
            const matchedJobs = (jobs ?? []).filter(j => j.phoneRaw === normalizedCaller);

            if (matchedJobs.length > 0) {
                callerIdNameSet(matchedJobs[0].fullName);
                relatedJobsSet(matchedJobs);
                return;
            }

            // 3) Nothing found
            callerIdNameSet(undefined);
            relatedJobsSet([]);
        });

        return () => {
            unsubscribe();
        }

    }, [])

    // useEffect(() => {



    //     if (claimedExts) {
    //         claimedExts.forEach(async ext => {
    //             try {

    //                 await phone.addLine({
    //                     id: `ext-${ext.id}`,
    //                     sipUri: `sip:${ext.extensionNumber}@${ext.wssHost}`,
    //                     wsServer: `wss://${ext.wssHost}:8089/ws`,
    //                     host: ext.wssHost ?? "",
    //                     authorizationUsername: `${ext.extensionNumber}`,
    //                     authorizationPassword: `${ext.secretPassword}`,
    //                     displayName: `${ext.name}`
    //                 });

    //             } catch (err) {
    //                 console.error("Phone registering error", err);
    //             }
    //         });
    //     }

    // }, [claimedExts])


    useEffect(() => {
        if (!isPhoneOwner) {
            phone.clearAllLines().catch(() => undefined);
            return;
        }

        if (!claimedExts || claimedExts.length < 1) {
            phone.clearAllLines().catch(() => undefined);
            return;
        }

        let cancelled = false;

        const syncLines = async () => {
            const expectedLineIds = claimedExts.map(ext => `ext-${ext.id}`);

            // Remove lines that should not exist anymore
            const currentLines = phone.getLines() ?? [];
            for (const line of currentLines) {
                if (!expectedLineIds.includes(line.id)) {
                    await phone.removeLine(line.id).catch(() => undefined);
                }
            }

            // Add missing lines
            for (const ext of claimedExts) {
                if (cancelled) return;

                const lineId = `ext-${ext.id}`;

                if (phone.hasLine(lineId)) {
                    continue;
                }

                try {
                    await phone.addLine({
                        id: lineId,
                        sipUri: `sip:${ext.extensionNumber}@${ext.wssHost}`,
                        wsServer: `wss://${ext.wssHost}:8089/ws`,
                        host: ext.wssHost ?? "",
                        authorizationUsername: `${ext.extensionNumber}`,
                        authorizationPassword: `${ext.secretPassword}`,
                        displayName: `${ext.name}`
                    });
                } catch (err) {
                    console.error("Phone registering error", err);
                }
            }
        };

        void syncLines();

        return () => {
            cancelled = true;
        };
    }, [claimedExts, isPhoneOwner]);


    // Whenever the set of claimed extensions changes (after initial /init has
    // already returned), refetch the latest call log scoped to those extensions.
    // Without this, claiming an extension shows an empty log until page reload.
    const claimedExtsKey = (claimedExts ?? []).map(e => e.id).join(",");
    useEffect(() => {
        if (!isPhoneOwner) return;
        if (!claimedExts || claimedExts.length === 0) {
            setLatestCalls([]);
            return;
        }
        hub.Get(API_ROUTES.Calls + `phone-latest?take=${CALLS_FIRST_PAGE_SIZE}`, (res: any) => {
            if (res?.success) {
                const rows = res.data ?? [];
                setLatestCalls(rows);
                // A full page back means there may be older calls to scroll to.
                callsHasMoreSet(rows.length >= CALLS_FIRST_PAGE_SIZE);
                loadingMoreRef.current = false;
            }
        });
    }, [claimedExtsKey, isPhoneOwner]);


    // Load this owner's blocked numbers whenever the phone becomes active (or after a block/unblock),
    // so every call row shows the right Block/Unblock state even across reopens.
    useEffect(() => {
        if (!isPhoneOwner) return;
        hub.Get(API_ROUTES.BlockList + "?size=1000", (res: any) => {
            if (!res?.success) return;
            const map: Record<string, BlockInfo> = {};
            (res.data ?? []).forEach((b: any) => {
                const key = normalizeBlockKey(b.phoneNumber);
                if (key) map[key] = { id: b.id, blockedByUserId: b.blockedByUserId };
            });
            blockedMapSet(map);
        });
    }, [isPhoneOwner, blockedRefresh, globalState.showPhone]);


    // Fetch the next (older) page of calls for the current extension(s) and append it.
    // Cursor is the CallDate of the oldest row we already hold, so it stays correct even
    // while new calls are pushed to the top in real time.
    const loadMoreCalls = () => {
        if (loadingMoreRef.current || !callsHasMore) return;
        const current = latestCalls ?? [];
        if (current.length === 0) return;
        const oldest = current[current.length - 1]?.callDate;
        if (!oldest) return;

        loadingMoreRef.current = true;
        callsLoadingMoreSet(true);

        const beforeIso = typeof oldest === "string" ? oldest : new Date(oldest).toISOString();
        const url = API_ROUTES.Calls + `phone-latest?take=${CALLS_PAGE_SIZE}&before=${encodeURIComponent(beforeIso)}`;
        hub.Get(url, (res: any) => {
            loadingMoreRef.current = false;
            callsLoadingMoreSet(false);
            if (res?.success) {
                const rows = res.data ?? [];
                appendLatestCalls(rows);
                callsHasMoreSet(rows.length >= CALLS_PAGE_SIZE);
            }
        });
    };


    useEffect(() => {
        if (lines && lines.length > 0) selectedLineSet(lines[0])
    }, [lines?.length])


    const normalizeToPhoneRaw = (value?: string): string | null => {
        if (!value) return null;

        let digits = value.replace(/\D/g, "");

        // Remove outbound prefix 9
        if (digits.length === 11 && digits.startsWith("9")) {
            digits = digits.slice(1);
        }

        // 10-digit US number
        if (digits.length === 10) {
            return `+1${digits}`;
        }

        // 11-digit US number with country code
        if (digits.length === 11 && digits.startsWith("1")) {
            return `+${digits}`;
        }

        return null;
    };

    const norm = (v: string) => v.replace(/\D/g, "").replace(/^1/, "");

    const isPhoneLikeKeyword = (value?: string) => {
        if (!value?.trim()) return false;
        if (/[a-z]/i.test(value)) return false;
        return /^[\d\s\-()+]+$/.test(value);
    };


    const delay = (ms: number) =>
        new Promise(resolve => setTimeout(resolve, ms));

    const waitForCallEstablished = async (
        phone: SipPhoneManager,
        callId: string,
        timeoutMs = 15000
    ) => {
        const startedAt = Date.now();

        while (Date.now() - startedAt < timeoutMs) {
            const call = phone.getCall(callId);

            if (!call) {
                throw new Error("Call not found.");
            }

            if (call.status === "established") {
                return;
            }

            if (call.status === "ended" || call.status === "failed") {
                throw new Error("Call ended before DTMF could be sent.");
            }

            await delay(200);
        }

        throw new Error("Timed out waiting for call to become established.");
    };

    const renderExtensionManagerBody = () => {
        if (exts.length === 0) {
            return (
                <div className="text-center pt-10">
                    There is no extension!
                </div>
            );
        }

        const claimedIds = new Set((claimedExts ?? []).map(c => c.id));
        const claimedList = exts.filter(e => claimedIds.has(e.id));
        const unclaimedList = exts.filter(e => !claimedIds.has(e.id));
        const reachedLimit = (claimedExts?.length ?? 0) >= MAX_CLAIMED_EXTS;

        return (
            <>
                {
                    claimedList.length > 0 &&
                    <>
                        <div className="fw-bold fs-7 mb-2">
                            Connected extensions:
                        </div>
                        {
                            claimedList.map(ex =>
                                <FlexRow key={`phone-claimed-ext-${ex.id}`} className="border-bottom py-5">
                                    <div className="me-2">
                                        <Icon icon="phone-rotary" type="solid" className="text-success" />
                                    </div>
                                    <div className="limited-text fs-7" title={ex.name} style={{ maxWidth: 200 }}>
                                        {ex.name}
                                    </div>
                                    <div className="ms-auto">
                                        <button type="button" className="btn btn-light-danger btn-sm"
                                            disabled={revokingExtId != undefined}
                                            onClick={e => {
                                                e.preventDefault();
                                                if (!ex.id) return;
                                                revokingExtIdSet(ex.id);
                                                hub.Post(API_ROUTES.RevokeExtension, {
                                                    ext: ex.id,
                                                    userId: user?.id
                                                }, res => {
                                                    if (res.success) {
                                                        toastSuccess("Extension has been disconnected!");
                                                    } else {
                                                        toastError(res.message);
                                                    }
                                                    revokingExtIdSet(undefined);
                                                });
                                            }}>
                                            {
                                                revokingExtId === ex.id ?
                                                    <Indicator text="Disconnecting..." />
                                                    :
                                                    <>Disconnect</>
                                            }
                                        </button>
                                    </div>
                                </FlexRow>
                            )
                        }
                    </>
                }

                {
                    unclaimedList.length > 0 &&
                    <>
                        <div className="fw-bold fs-7 mb-2 mt-4">
                            {claimedList.length > 0 ? "Other extensions:" : "Pick an extension to start using phone:"}
                        </div>
                        {
                            reachedLimit &&
                            <div className="fs-8 text-danger mb-2">
                                You have reached the maximum of {MAX_CLAIMED_EXTS} connected extensions.
                            </div>
                        }
                        {
                            unclaimedList.map(ex => {
                                const takenBy = claimers.find(c => c.extId === ex.id && c.userId !== user?.id);
                                return (
                                    <FlexRow key={`phone-claim-ext-${ex.id}`} className="border-bottom py-5">
                                        <div className="me-2">
                                            <Icon icon="phone-rotary" type="solid" className={takenBy ? "text-gray-500" : ""} />
                                        </div>
                                        <div>
                                            <div className="limited-text fs-7" title={ex.name} style={{ maxWidth: 200 }}>
                                                {ex.name}
                                            </div>
                                            {
                                                takenBy &&
                                                <>
                                                    <div className="fs-8 text-gray-700 mt-1">
                                                        Connected by {takenBy.userName}
                                                    </div>
                                                    <div className="fs-8 text-gray-600">
                                                        {formatClaimedAt(takenBy.claimedAt)}
                                                    </div>
                                                </>
                                            }
                                        </div>
                                        {
                                            !takenBy &&
                                            <div className="ms-auto">
                                                <button type="button" className="btn btn-primary btn-sm"
                                                    disabled={connectingToExt != undefined || reachedLimit}
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        if (reachedLimit) {
                                                            toastError(`You can connect up to ${MAX_CLAIMED_EXTS} extensions.`);
                                                            return;
                                                        }
                                                        connectingToExtSet(ex)
                                                        hub.Post(API_ROUTES.ClaimExtension, {
                                                            exts: ex.id,
                                                            userId: user?.id
                                                        }, res => {
                                                            if (res.success) {
                                                                toastSuccess("Extension has been connected successfully!")
                                                            }
                                                            else {
                                                                toastError(res.message)
                                                            }
                                                            connectingToExtSet(undefined)
                                                        })
                                                    }}>
                                                    {
                                                        connectingToExt?.id == ex.id ?
                                                            <Indicator text="Connecting..." />
                                                            :
                                                            <>Connect</>
                                                    }
                                                </button>
                                            </div>
                                        }
                                    </FlexRow>
                                );
                            })
                        }
                    </>
                }
            </>
        );
    };

    return (
        <>

            {
                globalState.showPhone &&
                <div ref={ref} className="float-phone-dialer bg-light"
                    onClick={async e => {
                        await phone.unlockAudio()
                    }}>

                    <FlexRow className="border-bottom mb-5 py-5" onMouseDown={onMouseDown}>

                        {
                            selectedTab == "contacts" && !phoneState.activeCall ?
                                <>
                                    <input ref={searchInput} type="text" className="flext-grow-1 w-100 phone-input-search"
                                        placeholder="Search..." value={searchKeyword}
                                        onChange={e => searchKeywordSet(e.target.value)} />
                                </>
                                :
                                <>
                                    {
                                        viewMode === "manage" ?
                                            <FlexRow>
                                                <a href="#" className="me-3"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        viewModeSet("phone");
                                                    }}>
                                                    <Icon icon="chevron-left" size={18} />
                                                </a>
                                                <div className="fw-bold">
                                                    Manage Extensions
                                                </div>
                                            </FlexRow>
                                            : selectedLine ?
                                                <div ref={extMenuRef} className="position-relative">
                                                    <FlexRow key={`active-line-stat-${selectedLine.id}`}
                                                        className="cursor-pointer"
                                                        onClick={() => {
                                                            extMenuOpenSet(v => !v);
                                                        }}>
                                                        <div className="me-2 d-flex align-items-center">
                                                            {renderLineStatusIcon(lines?.find(l => l.id === selectedLine.id) ?? selectedLine)}
                                                        </div>
                                                        <div className="fw-bold">
                                                            {selectedLine.displayName}
                                                        </div>
                                                        <div className="ms-2">
                                                            <Icon icon={extMenuOpen ? "chevron-up" : "chevron-down"} size={12} />
                                                        </div>
                                                    </FlexRow>
                                                    {
                                                        extMenuOpen &&
                                                        <div className="phone-ext-switcher-menu bg-light">
                                                            {
                                                                (claimedExts ?? []).map(ext => {
                                                                    const lineId = `ext-${ext.id}`;
                                                                    const line = (lines ?? []).find(l => l.id === lineId);
                                                                    const isActive = selectedLine.id === lineId;
                                                                    return (
                                                                        <FlexRow key={`ext-switch-${lineId}`}
                                                                            className={`phone-ext-switcher-item ${isActive ? "phone-ext-switcher-item-active" : ""}`}
                                                                            onClick={() => {
                                                                                if (line) {
                                                                                    selectedLineSet(line);
                                                                                }
                                                                                extMenuOpenSet(false);
                                                                            }}>
                                                                            <div className="me-2 d-flex align-items-center">
                                                                                {renderLineStatusIcon(line)}
                                                                            </div>
                                                                            <div className="fw-bold flex-grow-1">
                                                                                {line?.displayName ?? ext.name}
                                                                            </div>
                                                                            {
                                                                                isActive &&
                                                                                <Icon icon="check" className="ms-2 text-primary" />
                                                                            }
                                                                        </FlexRow>
                                                                    );
                                                                })
                                                            }
                                                            <FlexRow className="phone-ext-switcher-item border-top"
                                                                onClick={() => {
                                                                    selectedTabSet("settings");
                                                                    viewModeSet("manage");
                                                                    extMenuOpenSet(false);
                                                                }}>
                                                                <Icon icon="gear" type="solid" className="me-2" />
                                                                <div>Manage Extensions</div>
                                                            </FlexRow>
                                                        </div>
                                                    }
                                                </div>
                                                :
                                                <h3>
                                                    Phone
                                                </h3>
                                    }
                                </>
                        }

                        <IconButton icon="times" className="ms-auto"
                            onClick={e => {
                                closeBrowserPhone()
                            }} />

                    </FlexRow>

                    {
                        !isPhoneOwner ?
                            <div className="phone-tab-container">
                                <FlexRow column>

                                    <div className="fs-5 fw-bold mb-10 mt-2">
                                        Phone is currently being used in another tab
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={async e => {
                                            e.preventDefault();
                                            phoneTabs.claimOwnership(true);
                                        }}>
                                        Use Here
                                    </button>
                                </FlexRow>
                            </div>
                            : claimedExts && claimedExts.length > 0 ?
                                <>

                                    {
                                        phoneState.activeCall ?
                                            <ActiveCall call={phoneState.activeCall}
                                                phone={phone} callerId={callerIdName}
                                                jobs={relatedJobs} isTech={callerIdIsTech} />
                                            :
                                            <>

                                                {
                                                    selectedTab == "dial" &&
                                                    <div className="phone-tab-container">
                                                        <DialPad onDial={async (input) => {
                                                            const [mainNumberRaw, postDialRaw = ""] = input.split(",");
                                                            const mainNumber = mainNumberRaw?.trim();
                                                            const postDial = postDialRaw.replace(/[^\d#*A-Da-d]/g, "").toUpperCase();

                                                            if (!mainNumber) return;

                                                            const callId = await phone.dial(mainNumber, selectedLine?.id);

                                                            if (postDial) {
                                                                await waitForCallEstablished(phone, callId);

                                                                for (const ch of postDial) {
                                                                    await phone.sendDtmf(callId, ch);
                                                                    await delay(250);
                                                                }
                                                            }

                                                        }} />
                                                    </div>
                                                }

                                                {
                                                    selectedTab == "contacts" &&
                                                    <div className="phone-tab-container scroll-y">
                                                        {
                                                            (() => {
                                                                const keyword = searchKeyword.trim().toLowerCase();
                                                                const keywordNoHash = keyword.replace("#", "");
                                                                const kd = norm(searchKeyword);
                                                                const isPhoneSearch = isPhoneLikeKeyword(searchKeyword);

                                                                const techRows = (techs ?? [])
                                                                    .filter(f =>
                                                                        f.fullName.toLowerCase().includes(keyword) ||
                                                                        (!!kd && norm(f.phoneRaw ?? "").includes(kd))
                                                                    )
                                                                    .map(tech => ({
                                                                        kind: "tech" as const,
                                                                        id: `tech-${tech.id}`,
                                                                        sortName: tech.fullName ?? "",
                                                                        suspended: tech.suspended ?? false,
                                                                        tech
                                                                    }));

                                                                const jobRows = (jobs ?? [])
                                                                    .filter(f =>
                                                                        f.fullName?.toLowerCase().includes(keyword) ||
                                                                        f.jobId?.toLowerCase().includes(keyword) ||
                                                                        f.jobId?.toLowerCase().includes(keywordNoHash) ||
                                                                        f.address?.address?.label?.toLowerCase().includes(keyword) ||
                                                                        (isPhoneSearch && !!kd && norm(f.phoneRaw ?? "").includes(kd))
                                                                    )
                                                                    .map(job => ({
                                                                        kind: "job" as const,
                                                                        id: `job-${job.id}`,
                                                                        sortName: job.fullName ?? "",
                                                                        suspended: false,
                                                                        job
                                                                    }));

                                                                const merged = [...techRows, ...jobRows].sort((a, b) => {
                                                                    if (a.suspended !== b.suspended) {
                                                                        return (a.suspended ? 1 : 0) - (b.suspended ? 1 : 0);
                                                                    }
                                                                    return a.sortName.localeCompare(b.sortName, undefined, { sensitivity: "base" });
                                                                });

                                                                return merged.map(row => {
                                                                    const isTech = row.kind === "tech";
                                                                    const tech = isTech ? row.tech : undefined;
                                                                    const job = !isTech ? row.job : undefined;

                                                                    const phoneNumber = isTech ? tech?.phoneRaw : job?.phoneRaw;
                                                                    const phoneFriendly = isTech ? tech?.phoneItem?.phoneFriendly : job?.phone?.phoneFriendly;
                                                                    const fullName = isTech ? tech?.fullName : job?.fullName;
                                                                    const avatar = isTech ? tech?.avatar : undefined;
                                                                    const isExpanded = expandedContactId === row.id;

                                                                    const relatedJobs = isTech
                                                                        ? (jobs ?? []).filter(j => j.techId === tech?.id)
                                                                        : (jobs ?? []).filter(j => j.phoneRaw === job?.phoneRaw);

                                                                    return (
                                                                        <div key={row.id}
                                                                            className={`phone-call-card ${isExpanded ? "phone-call-card-expanded" : ""}`}>

                                                                            <FlexRow className="phone-call-row"
                                                                                onClick={() => expandedContactIdSet(prev => prev === row.id ? undefined : row.id)}>

                                                                                <div className="me-3">
                                                                                    <Avatar avatarUrl={avatar} fullName={fullName} width={34} />
                                                                                </div>

                                                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                                    <FlexRow>
                                                                                        <span className="phone-call-number limited-text me-2">
                                                                                            {fullName}
                                                                                        </span>
                                                                                        {
                                                                                            isTech ?
                                                                                                <span className="badge badge-light-primary fs-9 d-flex align-items-center">
                                                                                                    <Icon icon="user-helmet-safety" type="solid" size={9} className="me-1" />
                                                                                                    Tech
                                                                                                </span>
                                                                                                :
                                                                                                <span className="badge badge-light-info fs-9 d-flex align-items-center">
                                                                                                    <Icon icon="briefcase" type="solid" size={9} className="me-1" />
                                                                                                    Job
                                                                                                </span>
                                                                                        }
                                                                                        {
                                                                                            phoneNumber && blockedMap[normalizeBlockKey(phoneNumber)] &&
                                                                                            <span className="badge badge-light-danger fs-9 d-flex align-items-center ms-1">
                                                                                                <Icon icon="ban" type="solid" size={9} className="me-1" />
                                                                                                Blocked
                                                                                            </span>
                                                                                        }
                                                                                    </FlexRow>
                                                                                    <div className="fs-8 text-gray-700 mt-1">
                                                                                        {phoneFriendly ?? phoneNumber}
                                                                                    </div>
                                                                                    {
                                                                                        tech?.suspended &&
                                                                                        <div className="mt-1">
                                                                                            <span className="badge badge-light-danger fs-9">Suspended</span>
                                                                                        </div>
                                                                                    }
                                                                                </div>

                                                                                <Icon
                                                                                    icon="chevron-down"
                                                                                    size={12}
                                                                                    className={`text-gray-500 ms-2 phone-call-chevron ${isExpanded ? "phone-call-chevron-open" : ""}`}
                                                                                />
                                                                            </FlexRow>

                                                                            {
                                                                                isExpanded &&
                                                                                <div className="phone-call-expanded">
                                                                                    <FlexRow className="phone-call-actions" justify="around">
                                                                                        <div className="phone-call-action"
                                                                                            onClick={async e => {
                                                                                                e.stopPropagation();
                                                                                                if (!phoneNumber) return;
                                                                                                await phone.dial(phoneNumber, selectedLine?.id);
                                                                                            }}>
                                                                                            <div className="phone-call-action-icon phone-call-action-icon-primary">
                                                                                                <Icon icon="phone" type="solid" size={16} />
                                                                                            </div>
                                                                                            <div className="phone-call-action-label">Call</div>
                                                                                        </div>

                                                                                        <div className="phone-call-action"
                                                                                            onClick={e => {
                                                                                                e.stopPropagation();
                                                                                                if (!phoneNumber) return;
                                                                                                createJob(undefined, undefined, { phone: phoneNumber });
                                                                                            }}>
                                                                                            <div className="phone-call-action-icon phone-call-action-icon-success">
                                                                                                <Icon icon="plus" type="solid" size={16} />
                                                                                            </div>
                                                                                            <div className="phone-call-action-label">Add Job</div>
                                                                                        </div>

                                                                                        <div className="phone-call-action"
                                                                                            onClick={e => {
                                                                                                e.stopPropagation();
                                                                                                if (!phoneNumber) return;
                                                                                                const existing = chatList?.find(c => c.recipientPhone === phoneNumber);
                                                                                                closeBrowserPhone();
                                                                                                if (existing?.chatId) {
                                                                                                    navigate("/chat/" + existing.chatId);
                                                                                                } else {
                                                                                                    openNewChatForPhone(phoneNumber);
                                                                                                }
                                                                                            }}>
                                                                                            <div className="phone-call-action-icon phone-call-action-icon-info">
                                                                                                <Icon icon="comment-dots" type="solid" size={16} />
                                                                                            </div>
                                                                                            <div className="phone-call-action-label">Message</div>
                                                                                        </div>

                                                                                        {(() => {
                                                                                            const key = normalizeBlockKey(phoneNumber);
                                                                                            const info = key ? blockedMap[key] : undefined;
                                                                                            if (info) {
                                                                                                const canUnblock = hasRole(ROLES.BlockedNumbersUnblock) || info.blockedByUserId === user?.id;
                                                                                                return (
                                                                                                    <div className="phone-call-action"
                                                                                                        style={canUnblock ? undefined : { opacity: 0.4, cursor: "not-allowed" }}
                                                                                                        onClick={e => {
                                                                                                            e.stopPropagation();
                                                                                                            if (!phoneNumber || !canUnblock) return;
                                                                                                            blockConfirmSet({ number: phoneNumber, mode: "unblock", id: info.id });
                                                                                                        }}>
                                                                                                        <div className="phone-call-action-icon phone-call-action-icon-secondary">
                                                                                                            <Icon icon="unlock" type="solid" size={16} />
                                                                                                        </div>
                                                                                                        <div className="phone-call-action-label">Unblock</div>
                                                                                                    </div>
                                                                                                );
                                                                                            }
                                                                                            return (
                                                                                                <div className="phone-call-action"
                                                                                                    onClick={e => {
                                                                                                        e.stopPropagation();
                                                                                                        if (!phoneNumber) return;
                                                                                                        blockConfirmSet({ number: phoneNumber, mode: "block" });
                                                                                                    }}>
                                                                                                    <div className="phone-call-action-icon phone-call-action-icon-danger">
                                                                                                        <Icon icon="ban" type="solid" size={16} />
                                                                                                    </div>
                                                                                                    <div className="phone-call-action-label">Block</div>
                                                                                                </div>
                                                                                            );
                                                                                        })()}
                                                                                    </FlexRow>

                                                                                    {
                                                                                        relatedJobs.length > 0 &&
                                                                                        <div className="phone-call-related">
                                                                                            <div className="phone-call-related-title">
                                                                                                <Icon icon="briefcase" type="solid" className="me-1" size={11} />
                                                                                                {isTech ? "Tech Jobs" : "Jobs"} ({relatedJobs.length})
                                                                                            </div>
                                                                                            {
                                                                                                relatedJobs.map(rj =>
                                                                                                    <FlexRow key={`contact-relj-${row.id}-${rj.id}`}
                                                                                                        className="phone-call-related-item"
                                                                                                        onClick={e => {
                                                                                                            if (rj.id) {
                                                                                                                openJobQuickView(rj.id);
                                                                                                            }
                                                                                                        }}>
                                                                                                        <div className="fw-bold me-2 text-primary">#{rj.jobId}</div>
                                                                                                        <div className="limited-text flex-grow-1">{rj.fullName}</div>
                                                                                                        <Icon icon="chevron-right" size={10} className="text-gray-500" />
                                                                                                    </FlexRow>
                                                                                                )
                                                                                            }
                                                                                        </div>
                                                                                    }
                                                                                </div>
                                                                            }
                                                                        </div>
                                                                    );
                                                                });
                                                            })()
                                                        }
                                                    </div>
                                                }

                                                {
                                                    selectedTab == "settings" &&
                                                    <div className="phone-tab-container scroll-y">
                                                        {
                                                            viewMode === "manage" ?
                                                                renderExtensionManagerBody()
                                                                :
                                                                <FlexRow className="py-5 border-bottom cursor-pointer phone-settings-item"
                                                                    onClick={() => viewModeSet("manage")}>
                                                                    <div className="me-3">
                                                                        <Icon icon="phone-rotary" type="solid" size={18} />
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <div className="fw-bold">Manage Extensions</div>
                                                                        <div className="fs-8 text-gray-700">
                                                                            Connect or disconnect extensions
                                                                        </div>
                                                                    </div>
                                                                    <div className="ms-auto">
                                                                        <Icon icon="chevron-right" size={14} />
                                                                    </div>
                                                                </FlexRow>
                                                        }
                                                    </div>
                                                }

                                                {
                                                    selectedTab == "calls" &&
                                                    <div className="phone-tab-container scroll-y phone-calls-list"
                                                        onScroll={(e) => {
                                                            const el = e.currentTarget;
                                                            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
                                                                loadMoreCalls();
                                                            }
                                                        }}>
                                                        {
                                                            latestCalls?.sort((a, b) => +b.callDate! - +a.callDate!)
                                                                .map(call => {

                                                                    const missed = call.callType == CallTypes.Incoming && call.disPosition?.toUpperCase() != "ANSWERED";
                                                                    const phoneNumber = call.callType == CallTypes.Incoming ? call.source : call.destination;
                                                                    const techMatch = techs?.find(t => t.phoneRaw === phoneNumber);
                                                                    const matchedJob = (jobs ?? []).find(j => j.phoneRaw === phoneNumber);
                                                                    const relatedJobs = phoneNumber ? (jobs ?? []).filter(j => j.phoneRaw === phoneNumber) : [];
                                                                    const avatar: string | undefined = techMatch?.avatar;
                                                                    const avatarName: string | undefined = techMatch?.fullName ?? matchedJob?.fullName;
                                                                    const isExpanded = expandedCallId === call.id;
                                                                    const isOutgoing = call.callType == CallTypes.Outgoing;

                                                                    // Match the phone line that handled this call to surface its source/jobsource
                                                                    const matchedLine = (call.lineId ? phoneLines.find(p => p.id === call.lineId) : undefined)
                                                                        ?? (call.did ? phoneLines.find(p => p.phoneNumber === call.did) : undefined);

                                                                    // Look up the extension that handled the call
                                                                    const matchedExt = call.extensionId ? exts?.find(e => e.id === call.extensionId) : undefined;
                                                                    const extNumberLabel = matchedExt?.extensionNumber?.toString();

                                                                    // Build caller (left) / called (right) values + optional extension below each side
                                                                    let detailLeftMain: string | undefined;
                                                                    let detailLeftExt: string | undefined;
                                                                    let detailRightMain: string | undefined;
                                                                    let detailRightExt: string | undefined;

                                                                    if (isOutgoing) {
                                                                        // Outgoing: our line (DID) is the caller, dialed number is the recipient
                                                                        detailLeftMain = matchedLine?.phoneNumber ?? call.source;
                                                                        detailLeftExt = matchedLine?.phoneNumber ? (call.source || extNumberLabel) : extNumberLabel;
                                                                        detailRightMain = call.destination;
                                                                    } else {
                                                                        // Incoming: external caller, our DID/line is the recipient
                                                                        detailLeftMain = call.source;
                                                                        detailRightMain = call.did ?? matchedLine?.phoneNumber ?? call.destination;
                                                                        detailRightExt = extNumberLabel;
                                                                    }

                                                                    // Duration string (prefer billSec — actual talk time)
                                                                    const durationStr = call.billSecStr || call.durationStr;

                                                                    return (
                                                                        <div key={`phone-latest-call-${call.id}`}
                                                                            className={`phone-call-card ${isExpanded ? "phone-call-card-expanded" : ""} ${missed ? "phone-call-card-missed" : ""}`}>

                                                                            <FlexRow className="phone-call-row"
                                                                                onClick={() => expandedCallIdSet(prev => prev === call.id ? undefined : call.id)}>

                                                                                <div className="me-3">
                                                                                    <Avatar avatarUrl={avatar} fullName={avatarName} width={34} />
                                                                                </div>

                                                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                                    <FlexRow>
                                                                                        <div className={`phone-call-number ${missed ? "text-danger" : ""}`}>
                                                                                            {phoneNumber}
                                                                                        </div>
                                                                                        {
                                                                                            phoneNumber && blockedMap[normalizeBlockKey(phoneNumber)] &&
                                                                                            <Icon icon="ban" type="solid" size={12} className="text-danger ms-2 flex-shrink-0" />
                                                                                        }
                                                                                    </FlexRow>
                                                                                    {
                                                                                        avatarName &&
                                                                                        <FlexRow className={`fs-8 ${missed ? "text-danger" : "text-gray-700"}`}>
                                                                                            <Icon icon={techMatch ? "user-helmet-safety" : "briefcase"} type="solid" className="me-1" size={11} />
                                                                                            <span className="limited-text" style={{ maxWidth: 180 }}>{avatarName}</span>
                                                                                        </FlexRow>
                                                                                    }
                                                                                    <FlexRow className={`fs-8 mt-1 ${missed ? "text-danger" : "text-gray-600"}`}>
                                                                                        <Icon
                                                                                            icon={isOutgoing ? "arrow-up-right" : "arrow-down-left"}
                                                                                            type="solid"
                                                                                            className="me-1"
                                                                                            size={11}
                                                                                        />
                                                                                        <span>{toPrettyDate(call.callDate)}</span>
                                                                                    </FlexRow>
                                                                                </div>

                                                                                {
                                                                                    hasCallRecording &&
                                                                                    (Environment.instance.isDevelopment || ((call.disPosition == "ANSWERED" || call.callSource == CallSources.Twilio) && !window.isNullOrEmpty(call.recordingFile))) &&
                                                                                    (() => {
                                                                                        const isSelected = globalState.selectedCallToPlay?.id == call.id;
                                                                                        const isPlaying = isSelected && globalState.floatAudioStatus?.playing;
                                                                                        const isLoading = isSelected && globalState.floatAudioStatus?.loading;
                                                                                        return (
                                                                                            <a href="#"
                                                                                                title="Play Recording"
                                                                                                className="phone-call-play"
                                                                                                onClick={e => {
                                                                                                    e.preventDefault();
                                                                                                    e.stopPropagation();
                                                                                                    if (isSelected) {
                                                                                                        if (globalState.floatAudioStatus?.playing) pauseGlobalPlayer();
                                                                                                        else playGlobalPlayer();
                                                                                                    } else {
                                                                                                        playCall(call as unknown as Call);
                                                                                                    }
                                                                                                }}>
                                                                                                <Icon
                                                                                                    icon={isPlaying ? "pause" : isLoading ? "spinner" : "play"}
                                                                                                    type="solid"
                                                                                                    spin={isLoading}
                                                                                                    color="#555"
                                                                                                    size={15}
                                                                                                />
                                                                                            </a>
                                                                                        );
                                                                                    })()
                                                                                }

                                                                                <Icon
                                                                                    icon="chevron-down"
                                                                                    size={12}
                                                                                    className={`text-gray-500 ms-2 phone-call-chevron ${isExpanded ? "phone-call-chevron-open" : ""}`}
                                                                                />
                                                                            </FlexRow>

                                                                            {
                                                                                isExpanded &&
                                                                                <div className="phone-call-expanded">

                                                                                    <div className="phone-call-details">
                                                                                        <FlexRow className="phone-call-detail-line">
                                                                                            <div className="phone-call-detail-side phone-call-detail-side-left">
                                                                                                <div className="phone-call-detail-cap">From</div>
                                                                                                <div className="phone-call-detail-num">{detailLeftMain || "—"}</div>
                                                                                                {
                                                                                                    detailLeftExt && detailLeftExt !== detailLeftMain
                                                                                                        ? <div className="phone-call-detail-ext">ext. {detailLeftExt}</div>
                                                                                                        : <div className="phone-call-detail-ext phone-call-detail-ext-placeholder">&nbsp;</div>
                                                                                                }
                                                                                            </div>

                                                                                            <div className="phone-call-detail-mid">
                                                                                                <div className="phone-call-detail-duration">
                                                                                                    {durationStr || "—"}
                                                                                                </div>
                                                                                                {
                                                                                                    matchedLine?.jobSourceName &&
                                                                                                    <div className="phone-call-detail-mid-source">
                                                                                                        <Icon icon="bullhorn" type="solid" size={9} className="me-1" />
                                                                                                        {matchedLine.jobSourceName}
                                                                                                    </div>
                                                                                                }
                                                                                            </div>

                                                                                            <div className="phone-call-detail-side phone-call-detail-side-right">
                                                                                                <div className="phone-call-detail-cap">To</div>
                                                                                                <div className="phone-call-detail-num">{detailRightMain || "—"}</div>
                                                                                                {
                                                                                                    detailRightExt && detailRightExt !== detailRightMain
                                                                                                        ? <div className="phone-call-detail-ext">ext. {detailRightExt}</div>
                                                                                                        : <div className="phone-call-detail-ext phone-call-detail-ext-placeholder">&nbsp;</div>
                                                                                                }
                                                                                            </div>
                                                                                        </FlexRow>

                                                                                    </div>

                                                                                    <FlexRow className="phone-call-actions" justify="around">
                                                                                        <div className="phone-call-action"
                                                                                            onClick={async e => {
                                                                                                e.stopPropagation();
                                                                                                if (!phoneNumber) return;
                                                                                                await phone.dial(phoneNumber, selectedLine?.id);
                                                                                            }}>
                                                                                            <div className="phone-call-action-icon phone-call-action-icon-primary">
                                                                                                <Icon icon="phone" type="solid" size={16} />
                                                                                            </div>
                                                                                            <div className="phone-call-action-label">Call</div>
                                                                                        </div>

                                                                                        <div className="phone-call-action"
                                                                                            onClick={e => {
                                                                                                e.stopPropagation();
                                                                                                if (!phoneNumber) return;
                                                                                                closeBrowserPhone();
                                                                                                createJob(undefined, undefined, {
                                                                                                    phone: phoneNumber,
                                                                                                    jobSourceId: matchedLine?.jobSourceId
                                                                                                });
                                                                                            }}>
                                                                                            <div className="phone-call-action-icon phone-call-action-icon-success">
                                                                                                <Icon icon="plus" type="solid" size={16} />
                                                                                            </div>
                                                                                            <div className="phone-call-action-label">Add Job</div>
                                                                                        </div>

                                                                                        <div className="phone-call-action"
                                                                                            onClick={e => {
                                                                                                e.stopPropagation();
                                                                                                if (!phoneNumber) return;
                                                                                                const existing = chatList?.find(c => c.recipientPhone === phoneNumber);
                                                                                                closeBrowserPhone();
                                                                                                if (existing?.chatId) {
                                                                                                    navigate("/chat/" + existing.chatId);
                                                                                                } else {
                                                                                                    openNewChatForPhone(phoneNumber);
                                                                                                }
                                                                                            }}>
                                                                                            <div className="phone-call-action-icon phone-call-action-icon-info">
                                                                                                <Icon icon="comment-dots" type="solid" size={16} />
                                                                                            </div>
                                                                                            <div className="phone-call-action-label">Message</div>
                                                                                        </div>

                                                                                        {(() => {
                                                                                            const key = normalizeBlockKey(phoneNumber);
                                                                                            const info = key ? blockedMap[key] : undefined;
                                                                                            if (info) {
                                                                                                const canUnblock = hasRole(ROLES.BlockedNumbersUnblock) || info.blockedByUserId === user?.id;
                                                                                                return (
                                                                                                    <div className="phone-call-action"
                                                                                                        style={canUnblock ? undefined : { opacity: 0.4, cursor: "not-allowed" }}
                                                                                                        onClick={e => {
                                                                                                            e.stopPropagation();
                                                                                                            if (!phoneNumber || !canUnblock) return;
                                                                                                            blockConfirmSet({ number: phoneNumber, mode: "unblock", id: info.id });
                                                                                                        }}>
                                                                                                        <div className="phone-call-action-icon phone-call-action-icon-secondary">
                                                                                                            <Icon icon="unlock" type="solid" size={16} />
                                                                                                        </div>
                                                                                                        <div className="phone-call-action-label">Unblock</div>
                                                                                                    </div>
                                                                                                );
                                                                                            }
                                                                                            return (
                                                                                                <div className="phone-call-action"
                                                                                                    onClick={e => {
                                                                                                        e.stopPropagation();
                                                                                                        if (!phoneNumber) return;
                                                                                                        blockConfirmSet({ number: phoneNumber, mode: "block" });
                                                                                                    }}>
                                                                                                    <div className="phone-call-action-icon phone-call-action-icon-danger">
                                                                                                        <Icon icon="ban" type="solid" size={16} />
                                                                                                    </div>
                                                                                                    <div className="phone-call-action-label">Block</div>
                                                                                                </div>
                                                                                            );
                                                                                        })()}
                                                                                    </FlexRow>

                                                                                    {
                                                                                        relatedJobs.length > 0 &&
                                                                                        <div className="phone-call-related">
                                                                                            <div className="phone-call-related-title">
                                                                                                <Icon icon="briefcase" type="solid" className="me-1" size={11} />
                                                                                                Related Jobs ({relatedJobs.length})
                                                                                            </div>
                                                                                            {
                                                                                                relatedJobs.map(rj =>
                                                                                                    <FlexRow key={`call-relj-${call.id}-${rj.id}`}
                                                                                                        className="phone-call-related-item"
                                                                                                        onClick={e => {
                                                                                                            if (rj.id) {
                                                                                                                openJobQuickView(rj.id);
                                                                                                            }
                                                                                                        }}>
                                                                                                        <div className="fw-bold me-2 text-primary">#{rj.jobId}</div>
                                                                                                        <div className="limited-text flex-grow-1">{rj.fullName}</div>
                                                                                                        <Icon icon="chevron-right" size={10} className="text-gray-500" />
                                                                                                    </FlexRow>
                                                                                                )
                                                                                            }
                                                                                        </div>
                                                                                    }
                                                                                </div>
                                                                            }
                                                                        </div>
                                                                    )
                                                                })
                                                        }

                                                        {
                                                            callsLoadingMore &&
                                                            <FlexRow className="phone-calls-loading" style={{ justifyContent: "center", padding: "10px" }}>
                                                                <Icon icon="circle-notch" type="solid" spin size={16} />
                                                            </FlexRow>
                                                        }

                                                    </div>
                                                }

                                            </>
                                    }

                                    <FlexRow className="pb-5 border-top phone-dialer-tabs">
                                        <div
                                            className="phone-dialer-tab-indicator"
                                            style={{
                                                transform: `translateX(${Math.max(0, tabs.findIndex(t => t.id === selectedTab)) * 100}%)`
                                            }}
                                        />
                                        {
                                            tabs.map(t => {
                                                const showMissedBadge = t.id === "calls" && unseenMissed > 0
                                                return (
                                                    <FlexRow key={`phone-dialer-tab-${t.id}`} column
                                                        className={`phone-dialer-tab ${selectedTab == t.id ? "phone-dialer-active-tab" : ""}`}
                                                        onClick={e => {
                                                            e?.preventDefault()
                                                            if (t.id !== "settings" && viewMode === "manage") {
                                                                viewModeSet("phone");
                                                            }
                                                            selectedTabSet(t.id!)
                                                        }}>
                                                        <div className="mb-1 position-relative">
                                                            <Icon icon={t.icon} size={22} type="thin" />
                                                            {
                                                                showMissedBadge &&
                                                                <span className="position-absolute translate-middle badge badge-circle badge-danger" style={{
                                                                    top: 0,
                                                                    left: "100%",
                                                                    width: 16,
                                                                    height: 16,
                                                                    overflow: "hidden",
                                                                    lineHeight: 16,
                                                                    fontSize: 10
                                                                }}>
                                                                    {unseenMissed > 9 ? "9+" : unseenMissed}
                                                                </span>
                                                            }
                                                        </div>
                                                        <div>
                                                            {t.name}
                                                        </div>
                                                    </FlexRow>
                                                )
                                            })
                                        }
                                    </FlexRow>

                                </>
                                :
                                <div className="h-400px pt-2 scroll-y">
                                    {renderExtensionManagerBody()}
                                </div>
                    }
                </div>
            }

            {
                !globalState.showPhone && phoneState.activeCall &&
                <ActiveCallCompact call={phoneState.activeCall} phone={phone}
                    callerId={callerIdName} isTech={callerIdIsTech}
                    jobs={relatedJobs} />
            }

            {
                blockConfirm &&
                <Modal id="modal-phone-block"
                    title={blockConfirm.mode === "block" ? "Block Number" : "Unblock Number"}
                    open visible staticBackdrop
                    zIndex={3000}
                    buttonColor={blockConfirm.mode === "block" ? "danger" : "success"}
                    buttonText={blockConfirm.mode === "block" ? "Block" : "Unblock"}
                    onClose={() => blockConfirmSet(undefined)}
                    onConfirm={callback => {
                        if (blockConfirm.mode === "block") {
                            hub.Post(API_ROUTES.BlockList + "block", { phoneNumber: blockConfirm.number }, res => {
                                if (res.success) {
                                    toastSuccess(`${blockConfirm.number} has been blocked.`);
                                    blockedRefreshSet(x => x + 1);
                                } else {
                                    toastError(res.message ?? "Could not block this number.");
                                }
                                callback();
                            });
                        } else {
                            hub.Post(API_ROUTES.BlockList + "delete", [blockConfirm.id], res => {
                                if (res.success) {
                                    toastSuccess(`${blockConfirm.number} has been unblocked.`);
                                    blockedRefreshSet(x => x + 1);
                                } else {
                                    toastError(res.message ?? "Could not unblock this number.");
                                }
                                callback();
                            });
                        }
                    }}>
                    <div className="py-5 px-2">
                        {
                            blockConfirm.mode === "block"
                                ? <>Block <b>{blockConfirm.number}</b>? Incoming calls from this number will be rejected.</>
                                : <>Unblock <b>{blockConfirm.number}</b>? Calls from this number will ring through again.</>
                        }
                    </div>
                </Modal>
            }


        </>
    )
}